import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  FileText, Download, Star, Calendar, User, Mail, CreditCard, Loader2,
  LogOut, BarChart3, Clock, ChevronRight, Lock, Eye, ShieldCheck,
  Share2, Check, Menu, X, Settings, TrendingUp, MapPin, DollarSign,
  AlertTriangle, Zap, Search, MessageSquare,
} from "lucide-react";
import Logo from "@/components/Logo";
import TalkToExpert from "@/components/TalkToExpert";
import OnboardingWizard from "@/components/OnboardingWizard";
import ReviewManagementTab from "@/components/ReviewManagementTab";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STRIPE_GROWTH = "https://buy.stripe.com/eVqeVfd4133Q7tnf5E0RG01";
const STRIPE_DOMINATION = "https://buy.stripe.com/3cI4gBbZX47UcNH4r00RG02";
const STRIPE_REPUTATION = "https://buy.stripe.com/fZu8wRfc97k64hb9Lk0RG00";

const copyToClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement("textarea");
      el.value = text; el.style.cssText = "position:fixed;opacity:0";
      document.body.appendChild(el); el.select();
      document.execCommand("copy"); document.body.removeChild(el);
    });
  }
};

/* ═══════════════════ NAV CONFIG ═══════════════════ */
const NAV_MAIN = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "audits", label: "My Audits", icon: FileText },
  { key: "rankings", label: "My Rankings", icon: TrendingUp },
  { key: "reviews", label: "Review Management", icon: MessageSquare },
  { key: "reports", label: "My Reports", icon: Mail },
];
const NAV_ACCOUNT = [
  { key: "settings", label: "Settings", icon: Settings },
  { key: "billing", label: "Billing", icon: CreditCard },
];

/* ═══════════════════ SIDEBAR ═══════════════════ */
function Sidebar({ activePage, setActivePage, onLogout, mobileOpen, setMobileOpen }) {
  const inner = (
    <div className="flex flex-col h-full" style={{ background: "#0f172a" }}>
      {/* Logo */}
      <div className="p-5 flex items-center justify-between">
        <Logo size="small" theme="dark" />
        <button className="md:hidden text-white/60 hover:text-white" onClick={() => setMobileOpen(false)}>
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main Nav */}
      <div className="px-3 mt-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 px-3 mb-2">Main</p>
        {NAV_MAIN.map(item => (
          <button key={item.key} onClick={() => { setActivePage(item.key); setMobileOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mb-0.5 transition-colors"
            style={{
              background: activePage === item.key ? "rgba(99,102,241,0.15)" : "transparent",
              color: activePage === item.key ? "#a5b4fc" : "rgba(255,255,255,0.6)",
            }}
            data-testid={`nav-${item.key}`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </button>
        ))}
      </div>

      {/* Account Nav */}
      <div className="px-3 mt-6">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 px-3 mb-2">Account</p>
        {NAV_ACCOUNT.map(item => (
          <button key={item.key} onClick={() => { setActivePage(item.key); setMobileOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mb-0.5 transition-colors"
            style={{
              background: activePage === item.key ? "rgba(99,102,241,0.15)" : "transparent",
              color: activePage === item.key ? "#a5b4fc" : "rgba(255,255,255,0.6)",
            }}
            data-testid={`nav-${item.key}`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </button>
        ))}
        <button onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mt-1 transition-colors"
          style={{ color: "rgba(255,255,255,0.4)" }}
          data-testid="nav-logout"
        >
          <LogOut className="w-4 h-4" />Logout
        </button>
      </div>

      {/* Upgrade CTA */}
      <div className="mt-auto p-4">
        <div style={{ background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 12, padding: 16 }}>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4" style={{ color: "#a5b4fc" }} />
            <span className="text-sm font-bold text-white">Upgrade to Growth</span>
          </div>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
            Get more rankings & reviews
          </p>
          <a href={STRIPE_GROWTH} target="_blank" rel="noopener noreferrer"
            className="block text-center text-xs font-bold py-2 rounded-lg"
            style={{ background: "#6366f1", color: "#fff" }}
            data-testid="sidebar-upgrade-btn"
          >
            Upgrade Now — $497/mo
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:block w-60 shrink-0 min-h-screen" data-testid="dashboard-sidebar">{inner}</aside>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64">{inner}</div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════ DASHBOARD HOME ═══════════════════ */
function DashboardHome({ reports, usage, navigate, latestScore, totalLoss, paidCount, userEmail, token }) {
  const latestReport = reports.length > 0 ? reports[0] : null;
  const latestPaidReport = reports.find(r => r.status === "paid");
  return (
    <>
      <h1 className="text-2xl font-bold text-[#1e293b] mb-1" data-testid="dashboard-title">Welcome back</h1>
      <p className="text-sm text-[#64748b] mb-6">Here's your local SEO performance</p>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { icon: FileText, label: "Audits Run", value: reports.length, color: "#3B82F6" },
          { icon: TrendingUp, label: "Latest Score", value: latestScore ? `${latestScore}/100` : "—", color: "#F59E0B" },
          { icon: DollarSign, label: "Revenue At Risk", value: totalLoss ? `$${totalLoss.toLocaleString()}/month` : "—", color: "#EF4444" },
          { icon: ShieldCheck, label: "Plan", value: "FREE", color: "#6366f1", action: true },
        ].map((s, i) => (
          <Card key={i} className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: s.color + "10" }}>
                  <s.icon className="w-5 h-5" style={{ color: s.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-[#64748b]">{s.label}</p>
                  <p className="text-lg font-bold text-[#1e293b] truncate" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                    {s.value}
                  </p>
                </div>
              </div>
              {s.action && (
                <a href={STRIPE_REPUTATION} target="_blank" rel="noopener noreferrer"
                  className="text-xs font-medium mt-2 inline-block" style={{ color: "#6366f1" }}>
                  Upgrade <ChevronRight className="w-3 h-3 inline" />
                </a>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Onboarding Wizard for paid clients */}
      {latestPaidReport?.place_id && (
        <OnboardingWizard placeId={latestPaidReport.place_id} token={token} />
      )}

      {/* Run New Audit */}
      <Card className="bg-white border-slate-200 shadow-sm mb-8" data-testid="run-new-audit">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#6366f110" }}>
              <Search className="w-5 h-5" style={{ color: "#6366f1" }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#1e293b]">Run a New Audit</p>
              <p className="text-xs text-[#64748b]">Search for any business to start a fresh audit</p>
            </div>
            <Button onClick={() => navigate("/audit")} className="text-white text-sm" style={{ background: "#6366f1" }} data-testid="new-audit-btn">
              <Search className="w-4 h-4 mr-2" /> New Audit
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Request a Callback */}
      <TalkToExpert
        placeId={latestReport?.place_id || ""}
        email={userEmail || ""}
        className="mb-8"
      />

      {/* Recent Audits */}
      <h2 className="text-lg font-bold text-[#1e293b] mb-3">Recent Audits</h2>
      {reports.length === 0 ? (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-[#94a3b8]" />
            <p className="font-semibold mb-2 text-[#1e293b]">No reports yet</p>
            <p className="text-sm mb-6 text-[#64748b]">Run your first audit to see it appear here.</p>
            <Button onClick={() => navigate("/audit")} className="text-white" style={{ background: "#6366f1" }}>Run Your First Audit</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Business</TableHead><TableHead>Score</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {reports.slice(0, 5).map(r => (
                  <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                    <TableCell className="font-medium text-sm">{r.business_name}</TableCell>
                    <TableCell><ScoreBadge score={r.score} /></TableCell>
                    <TableCell className="text-sm text-[#64748b]">{fmtDate(r.created_at)}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/report/${r.place_id}`)} className="text-xs" style={{ color: "#6366f1" }}>
                        View <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </>
  );
}

/* ═══════════════════ MY AUDITS ═══════════════════ */
function MyAudits({ reports, navigate, handleUnlock, handleDownloadPdf, handleShareToggle, handleCopyShareLink, checkoutLoading, downloadingPdf, sharingReport, copiedToken }) {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-[#1e293b]">My Audits</h1>
        <Button onClick={() => navigate("/audit")} className="text-white" style={{ background: "#6366f1" }} data-testid="new-audit-btn">
          <BarChart3 className="w-4 h-4 mr-2" />New Audit
        </Button>
      </div>
      {reports.length === 0 ? (
        <Card className="bg-white border-slate-200"><CardContent className="p-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-[#94a3b8]" />
          <p className="font-semibold text-[#1e293b] mb-2">No audits yet</p>
          <Button onClick={() => navigate("/audit")} className="text-white mt-4" style={{ background: "#6366f1" }}>Run New Audit</Button>
        </CardContent></Card>
      ) : (
        <Card className="bg-white border-slate-200 overflow-hidden">
          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Business</TableHead><TableHead>Score</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {reports.map(r => (
                  <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                    <TableCell>
                      <p className="font-medium text-sm text-[#1e293b]">{r.business_name}</p>
                      <p className="text-xs text-[#94a3b8]">{r.business_address?.slice(0, 50)}</p>
                    </TableCell>
                    <TableCell><ScoreBadge score={r.score} /></TableCell>
                    <TableCell className="text-sm text-[#64748b]"><Calendar className="w-3.5 h-3.5 inline mr-1" />{fmtDate(r.created_at)}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/report/${r.place_id}`)} className="text-xs"><Eye className="w-3.5 h-3.5 mr-1" />View</Button>
                        <Button variant="ghost" size="sm" onClick={() => r.share_enabled && r.share_token ? handleCopyShareLink(r.share_token) : handleShareToggle(r.place_id)} disabled={sharingReport === r.place_id} className="text-xs">
                          {copiedToken === r.place_id || copiedToken === r.share_token ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Share2 className="w-3.5 h-3.5" />}
                        </Button>
                        {r.status === "paid" ? (
                          <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(r.place_id, r.business_name)} disabled={downloadingPdf === r.place_id} className="text-xs">
                            {downloadingPdf === r.place_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}PDF
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => handleUnlock(r.place_id)} disabled={checkoutLoading === r.place_id} className="text-white text-xs" style={{ background: "#6366f1" }}>
                            {checkoutLoading === r.place_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />}Unlock $27
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Mobile */}
          <div className="md:hidden divide-y divide-slate-200">
            {reports.map(r => (
              <div key={r.id} className="p-4 space-y-3">
                <div className="flex justify-between"><div><p className="font-medium text-sm">{r.business_name}</p><p className="text-xs text-[#94a3b8]">{fmtDate(r.created_at)}</p></div><div className="flex gap-2"><ScoreBadge score={r.score} /><StatusBadge status={r.status} /></div></div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/report/${r.place_id}`)} className="text-xs flex-1"><Eye className="w-3.5 h-3.5 mr-1" />View</Button>
                  {r.status === "paid" ? (
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(r.place_id, r.business_name)} className="text-xs flex-1"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
                  ) : (
                    <Button size="sm" onClick={() => handleUnlock(r.place_id)} className="text-white text-xs flex-1" style={{ background: "#6366f1" }}><CreditCard className="w-3.5 h-3.5 mr-1" />Unlock $27</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

/* ═══════════════════ MY RANKINGS ═══════════════════ */
function MyRankings({ reports, navigate }) {
  const latest = reports.find(r => r.status === "paid") || reports[0];
  return (
    <>
      <h1 className="text-2xl font-bold text-[#1e293b] mb-1">My Rankings</h1>
      <p className="text-sm text-[#64748b] mb-6">Rankings data updates with each audit</p>
      {!latest ? (
        <EmptyState icon={TrendingUp} title="No ranking data" desc="Run an audit to see your local search rankings." action={() => navigate("/audit")} actionLabel="Run New Audit" />
      ) : (
        <div className="space-y-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-5 h-5" style={{ color: "#6366f1" }} />
                <div><p className="font-bold text-[#1e293b]">{latest.business_name}</p><p className="text-xs text-[#64748b]">Latest audit: {fmtDate(latest.created_at)}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg"><p className="text-xs text-[#64748b] mb-1">Audit Score</p><p className="text-2xl font-bold" style={{ color: latest.score >= 70 ? "#22c55e" : latest.score >= 40 ? "#f59e0b" : "#ef4444" }}>{latest.score}/100</p></div>
                <div className="p-4 bg-slate-50 rounded-lg"><p className="text-xs text-[#64748b] mb-1">Status</p><p className="text-lg font-bold text-[#1e293b]">{latest.status === "paid" ? "Full Report" : "Free Preview"}</p></div>
              </div>
              <Button onClick={() => navigate(`/report/${latest.place_id}`)} className="w-full mt-4 text-white" style={{ background: "#6366f1" }}>
                View Full Rankings Report <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
          {latest.created_at && daysSince(latest.created_at) > 7 && (
            <div className="flex items-center gap-3 p-4 rounded-lg" style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}>
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div><p className="text-sm font-medium text-amber-800">Your ranking data is over 7 days old</p><p className="text-xs text-amber-700">Run a new audit to get updated rankings.</p></div>
              <Button size="sm" onClick={() => navigate("/audit")} className="ml-auto text-white shrink-0" style={{ background: "#f59e0b" }}>Update Now</Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/* ═══════════════════ MY REVIEWS ═══════════════════ */
function MyReviews({ reports, navigate }) {
  const latest = reports.find(r => r.status === "paid") || reports[0];
  return (
    <>
      <h1 className="text-2xl font-bold text-[#1e293b] mb-1">My Reviews</h1>
      <p className="text-sm text-[#64748b] mb-6">Your Google review performance</p>
      {!latest ? (
        <EmptyState icon={Star} title="No review data" desc="Run an audit to analyze your reviews." action={() => navigate("/audit")} actionLabel="Run New Audit" />
      ) : (
        <div className="space-y-4">
          <Card className="bg-white border-slate-200"><CardContent className="p-6">
            <p className="font-bold text-[#1e293b] mb-4">{latest.business_name}</p>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-center"><p className="text-3xl font-bold text-[#1e293b]">{latest.rating || "—"}</p><div className="flex gap-0.5 mt-1">{[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4" fill={s <= Math.round(latest.rating || 0) ? "#FBBF24" : "none"} stroke={s <= Math.round(latest.rating || 0) ? "#FBBF24" : "#D1D5DB"} />)}</div></div>
              <div><p className="text-lg font-bold text-[#1e293b]">{latest.review_count || "—"} reviews</p><p className="text-xs text-[#64748b]">on Google Business Profile</p></div>
            </div>
            <Button onClick={() => navigate(`/report/${latest.place_id}`)} variant="outline" className="w-full">View Full Review Analysis</Button>
          </CardContent></Card>
          <div className="rounded-xl p-5" style={{ background: "#0f172a" }}>
            <p className="text-sm font-bold text-white mb-1">Manage your reviews with Growth plan</p>
            <p className="text-xs text-slate-400 mb-3">Get automated review requests, response templates, and competitor monitoring.</p>
            <a href={STRIPE_GROWTH} target="_blank" rel="noopener noreferrer" className="inline-block text-xs font-bold py-2 px-4 rounded-lg text-white" style={{ background: "#6366f1" }}>
              Upgrade to Growth — $497/mo
            </a>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════ SETTINGS ═══════════════════ */
function SettingsPage({ userData, handleChangePassword, pwForm, setPwForm, pwLoading }) {
  return (
    <>
      <h1 className="text-2xl font-bold text-[#1e293b] mb-6">Settings</h1>
      <div className="space-y-6 max-w-lg">
        <Card className="bg-white border-slate-200"><CardContent className="p-6">
          <h3 className="font-bold text-[#1e293b] mb-4">Profile</h3>
          <div className="space-y-3">
            <div><label className="text-xs text-[#64748b] block mb-1">Name</label><input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 text-[#1e293b]" value={userData?.name || userData?.email?.split("@")[0] || ""} readOnly /></div>
            <div><label className="text-xs text-[#64748b] block mb-1">Email</label><input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 text-[#1e293b]" value={userData?.email || ""} readOnly /></div>
          </div>
        </CardContent></Card>
        <Card className="bg-white border-slate-200"><CardContent className="p-6">
          <h3 className="font-bold text-[#1e293b] mb-4">Change Password</h3>
          <div className="space-y-3">
            <div><label className="text-xs text-[#64748b] block mb-1">Current Password</label><input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366f1]" placeholder="Enter current password" /></div>
            <div><label className="text-xs text-[#64748b] block mb-1">New Password</label><input type="password" value={pwForm.newPw} onChange={e => setPwForm(f => ({ ...f, newPw: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366f1]" placeholder="Min 8 characters" /></div>
            <div><label className="text-xs text-[#64748b] block mb-1">Confirm Password</label><input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366f1]" placeholder="Repeat new password" onKeyDown={e => e.key === "Enter" && handleChangePassword()} /></div>
            <Button onClick={handleChangePassword} disabled={pwLoading} className="text-white" style={{ background: "#6366f1" }}>
              {pwLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Update Password"}
            </Button>
          </div>
        </CardContent></Card>
      </div>
    </>
  );
}

/* ═══════════════════ BILLING ═══════════════════ */
function BillingPage({ reports, paidCount }) {
  return (
    <>
      <h1 className="text-2xl font-bold text-[#1e293b] mb-6">Billing</h1>
      <div className="space-y-6 max-w-2xl">
        <Card className="bg-white border-slate-200"><CardContent className="p-6">
          <h3 className="font-bold text-[#1e293b] mb-1">Current Plan</h3>
          <p className="text-sm text-[#64748b] mb-4">Pay Per Report — $27 per unlock</p>
          <div className="flex items-center gap-3"><Badge className="bg-slate-100 text-[#1e293b] border-slate-200 text-sm px-3 py-1">FREE</Badge><span className="text-sm text-[#64748b]">{paidCount} report{paidCount !== 1 ? "s" : ""} unlocked</span></div>
        </CardContent></Card>

        {paidCount > 0 && (
          <Card className="bg-white border-slate-200"><CardContent className="p-6">
            <h3 className="font-bold text-[#1e293b] mb-3">Payment History</h3>
            <Table><TableHeader><TableRow><TableHead>Description</TableHead><TableHead>Amount</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
              <TableBody>
                {reports.filter(r => r.status === "paid").map((r, i) => (
                  <TableRow key={i}><TableCell className="text-sm">{r.business_name} — Full Report</TableCell><TableCell className="text-sm font-medium">$27</TableCell><TableCell className="text-sm text-[#64748b]">{fmtDate(r.created_at)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        )}

        <h3 className="font-bold text-[#1e293b] text-lg">Upgrade Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { name: "Reputation Essentials", price: "$97", link: STRIPE_REPUTATION, color: "#3B82F6", features: ["GBP optimization", "Review management", "Citation cleanup"] },
            { name: "Growth", price: "$497", link: STRIPE_GROWTH, color: "#10B981", popular: true, features: ["Everything in Essentials", "Content creation", "Local link building", "Competitor monitoring"] },
            { name: "Market Domination", price: "$997", link: STRIPE_DOMINATION, color: "#8B5CF6", features: ["Everything in Growth", "AI search optimization", "Dedicated strategist", "Priority support"] },
          ].map((plan, i) => (
            <Card key={i} className="bg-white border-slate-200 relative" style={plan.popular ? { border: "2px solid #10B981" } : {}}>
              {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#10B981] text-white text-[10px] font-bold px-3 py-1 rounded">MOST POPULAR</div>}
              <CardContent className="p-5">
                <p className="font-bold text-sm mb-1" style={{ color: plan.color }}>{plan.name}</p>
                <p className="text-2xl font-bold text-[#1e293b] mb-3">{plan.price}<span className="text-sm font-normal text-[#64748b]">/mo</span></p>
                {plan.features.map((f, j) => <div key={j} className="flex items-center gap-2 mb-1"><Check className="w-3.5 h-3.5" style={{ color: plan.color }} /><span className="text-xs text-[#64748b]">{f}</span></div>)}
                <a href={plan.link} target="_blank" rel="noopener noreferrer" className="block text-center text-sm font-bold py-2.5 rounded-lg mt-4 text-white" style={{ background: plan.color }}>Get Started</a>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-xs text-center text-[#94a3b8]">Your $27 report fee is credited toward your first month on any monthly plan.</p>
      </div>
    </>
  );
}

/* ═══════════════════ HELPERS ═══════════════════ */
function ScoreBadge({ score }) {
  const color = score >= 70 ? "#22c55e" : score >= 40 ? "#f59e0b" : "#ef4444";
  return <Badge style={{ background: color + "15", color, border: `1px solid ${color}30` }}>{score}/100</Badge>;
}
function StatusBadge({ status }) {
  return status === "paid"
    ? <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20"><ShieldCheck className="w-3 h-3 mr-1" />Paid</Badge>
    : <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20"><Lock className="w-3 h-3 mr-1" />Free</Badge>;
}
function EmptyState({ icon: Icon, title, desc, action, actionLabel }) {
  return (<Card className="bg-white border-slate-200"><CardContent className="p-12 text-center"><Icon className="w-12 h-12 mx-auto mb-4 text-[#94a3b8]" /><p className="font-semibold text-[#1e293b] mb-2">{title}</p><p className="text-sm text-[#64748b] mb-4">{desc}</p>{action && <Button onClick={action} className="text-white" style={{ background: "#6366f1" }}>{actionLabel}</Button>}</CardContent></Card>);
}
function fmtDate(d) { return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"; }
function daysSince(d) { return Math.floor((Date.now() - new Date(d).getTime()) / 86400000); }

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
export default function ClientDashboard() {
  const { user, token: authToken, getAuthHeaders, logout } = useAuth();
  const _urlParams = new URLSearchParams(window.location.search);
  const _impToken = _urlParams.get("impersonate_token");
  const token = _impToken || authToken;
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [sharingReport, setSharingReport] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/user/dashboard`, { headers: getAuthHeaders() });
      setDashboardData(res.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); navigate("/login?redirect=/dashboard"); }
      else toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  }, [token, getAuthHeaders, logout, navigate]);

  useEffect(() => { if (!token) { navigate("/login?redirect=/dashboard"); return; } fetchDashboard(); }, [token, navigate, fetchDashboard]);

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
    if (pwForm.newPw.length < 8) { toast.error("Min 8 characters"); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.newPw }, { headers: getAuthHeaders() });
      toast.success("Password changed"); setPwForm({ current: "", newPw: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setPwLoading(false); }
  };

  const handleDownloadPdf = async (placeId, name) => {
    setDownloadingPdf(placeId);
    try {
      const res = await axios.get(`${API}/report/pdf/${placeId}`, { headers: getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement("a"); a.href = url; a.download = `rankify-${name?.replace(/\s+/g, "-").toLowerCase() || "report"}.pdf`; a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); } finally { setDownloadingPdf(null); }
  };
  const handleUnlockReport = async (placeId) => {
    setCheckoutLoading(placeId);
    try {
      const res = await axios.post(`${API}/checkout/create`, { package_id: "recommendation-report", place_id: placeId, origin_url: window.location.origin }, { headers: getAuthHeaders() });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } finally { setCheckoutLoading(null); }
  };
  const handleShareToggle = async (placeId) => {
    setSharingReport(placeId);
    try {
      const res = await axios.post(`${API}/user/reports/${placeId}/share`, {}, { headers: getAuthHeaders() });
      if (res.data.share_enabled && res.data.share_token) { const shareUrl = `${window.location.origin}/shared/${res.data.share_token}`; await copyToClipboard(shareUrl); setCopiedToken(placeId); setTimeout(() => setCopiedToken(null), 2000); toast.success("Share link copied!"); }
      fetchDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } finally { setSharingReport(null); }
  };
  const handleCopyShareLink = async (shareToken) => { const url = `${window.location.origin}/shared/${shareToken}`; await copyToClipboard(url); setCopiedToken(shareToken); setTimeout(() => setCopiedToken(null), 2000); toast.success("Link copied!"); };

  if (loading) {
    return (<div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-center"><Logo size="default" theme="light" /><Loader2 className="w-8 h-8 animate-spin mx-auto mt-4 mb-3" style={{ color: "#6366f1" }} /><p className="text-[#64748b]">Loading dashboard...</p></div></div>);
  }

  const { reports = [], total_reports = 0, paid_reports = 0 } = dashboardData || {};
  const userData = dashboardData?.user || {};
  const latestReport = reports[0];
  const latestScore = latestReport?.score || null;
  const totalLoss = latestReport?.revenue_loss || null;

  const renderPage = () => {
    switch (activePage) {
      case "audits": return <MyAudits reports={reports} navigate={navigate} handleUnlock={handleUnlockReport} handleDownloadPdf={handleDownloadPdf} handleShareToggle={handleShareToggle} handleCopyShareLink={handleCopyShareLink} checkoutLoading={checkoutLoading} downloadingPdf={downloadingPdf} sharingReport={sharingReport} copiedToken={copiedToken} />;
      case "rankings": return <MyRankings reports={reports} navigate={navigate} />;
      case "reviews": return <ReviewManagementTab token={token} />;
      case "reports": return <MyAudits reports={reports} navigate={navigate} handleUnlock={handleUnlockReport} handleDownloadPdf={handleDownloadPdf} handleShareToggle={handleShareToggle} handleCopyShareLink={handleCopyShareLink} checkoutLoading={checkoutLoading} downloadingPdf={downloadingPdf} sharingReport={sharingReport} copiedToken={copiedToken} />;
      case "settings": return <SettingsPage userData={userData} handleChangePassword={handleChangePassword} pwForm={pwForm} setPwForm={setPwForm} pwLoading={pwLoading} />;
      case "billing": return <BillingPage reports={reports} paidCount={paid_reports} />;
      default: return <DashboardHome reports={reports} usage={dashboardData?.usage} navigate={navigate} latestScore={latestScore} totalLoss={totalLoss} paidCount={paid_reports} userEmail={user?.email} token={token} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="client-dashboard">
      <Sidebar activePage={activePage} setActivePage={setActivePage} onLogout={() => { logout(); navigate("/"); }} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-6 h-6 text-[#1e293b]" /></button>
          <Logo size="small" theme="light" />
          <div className="w-6" />
        </header>

        {/* Main content */}
        <main className="flex-1 p-6 md:p-8 lg:p-10 max-w-5xl">
          <motion.div key={activePage} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
            {renderPage()}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
